/**
 * ResetPasswordPage — public page reached via /reset-password/{token}
 *
 * Flow:
 *   1. On mount, verify the token via GET /api/auth/password-reset/verify/{token}
 *   2. If valid → show form, user enters new password + confirmation
 *   3. On submit → POST /api/auth/password-reset/confirm → redirect to /login
 */
import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import {
  Loader2, KeyRound, Check, AlertTriangle, Eye, EyeOff, ShieldCheck,
} from 'lucide-react';
import { API_URL } from '../utils/api';

export default function ResetPasswordPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState('verifying'); // verifying / ready / expired / submitting / done
  const [email, setEmail] = useState('');
  const [expiresAt, setExpiresAt] = useState(null);
  const [pwd, setPwd] = useState('');
  const [pwd2, setPwd2] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/auth/password-reset/verify/${token}`);
        if (cancelled) return;
        setEmail(data.email || '');
        setExpiresAt(data.expires_at ? new Date(data.expires_at) : null);
        setState('ready');
      } catch (err) {
        if (cancelled) return;
        setError(err?.response?.data?.detail || 'Lien invalide ou expiré');
        setState('expired');
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const pwdValid = pwd.length >= 8 && /[A-Z]/.test(pwd) && /\d/.test(pwd);
  const pwdMatch = pwd && pwd === pwd2;
  const canSubmit = pwdValid && pwdMatch && state === 'ready';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setState('submitting'); setError('');
    try {
      await axios.post(`${API_URL}/api/auth/password-reset/confirm`, {
        token,
        new_password: pwd,
      });
      setState('done');
      setTimeout(() => navigate('/login', { replace: true }), 2200);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Impossible de réinitialiser le mot de passe');
      setState('ready');
    }
  };

  const remaining = () => {
    if (!expiresAt) return '';
    const ms = expiresAt - new Date();
    if (ms <= 0) return 'expiré';
    const min = Math.round(ms / 60000);
    return `${min} min`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/40 to-violet-50/40 flex items-center justify-center p-4" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        {/* Brand */}
        <div className="text-center mb-6">
          <Link to="/login" className="inline-flex items-center gap-2 text-slate-400 hover:text-slate-600 text-sm">
            <span className="font-semibold">MediPocket</span>
          </Link>
        </div>

        <div className="bg-white rounded-3xl shadow-xl p-8 border border-slate-100">
          {state === 'verifying' && (
            <div className="text-center py-10">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-500 mx-auto mb-4" />
              <p className="text-sm text-slate-500">Vérification du lien…</p>
            </div>
          )}

          {state === 'expired' && (
            <div className="text-center py-6" data-testid="reset-expired">
              <div className="w-14 h-14 rounded-2xl bg-rose-50 border border-rose-200 flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-7 h-7 text-rose-500" />
              </div>
              <h1 className="text-xl font-bold text-slate-800 mb-2 font-['Outfit']">Lien invalide</h1>
              <p className="text-sm text-slate-500 mb-6">{error}</p>
              <p className="text-xs text-slate-400 mb-6">Demandez à l'administrateur de vous renvoyer un nouveau lien.</p>
              <Link to="/login" className="inline-block px-5 py-2.5 text-sm font-medium text-indigo-600 hover:bg-indigo-50 rounded-full transition-colors">
                Retour à la connexion
              </Link>
            </div>
          )}

          {state === 'done' && (
            <div className="text-center py-6" data-testid="reset-done">
              <div className="w-14 h-14 rounded-2xl bg-emerald-50 border border-emerald-200 flex items-center justify-center mx-auto mb-4">
                <Check className="w-7 h-7 text-emerald-500" />
              </div>
              <h1 className="text-xl font-bold text-slate-800 mb-2 font-['Outfit']">Mot de passe modifié</h1>
              <p className="text-sm text-slate-500">Redirection vers la connexion…</p>
              <Loader2 className="w-5 h-5 animate-spin text-indigo-400 mx-auto mt-4" />
            </div>
          )}

          {(state === 'ready' || state === 'submitting') && (
            <form onSubmit={handleSubmit} data-testid="reset-form">
              <div className="text-center mb-6">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center mx-auto mb-4 shadow-md">
                  <KeyRound className="w-7 h-7 text-white" />
                </div>
                <h1 className="text-xl font-bold text-slate-800 mb-1 font-['Outfit']" style={{ letterSpacing: '-0.01em' }}>
                  Nouveau mot de passe
                </h1>
                <p className="text-sm text-slate-500">{email}</p>
                {expiresAt && (
                  <p className="text-[11px] text-amber-600 mt-1">
                    Lien valide encore {remaining()}
                  </p>
                )}
              </div>

              {error && (
                <div className="flex items-start gap-2 p-3 bg-rose-50 border border-rose-200 rounded-xl text-sm text-rose-700 mb-4">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              <label className="block mb-3">
                <span className="text-xs text-slate-600 font-medium">Nouveau mot de passe</span>
                <div className="relative mt-1">
                  <input
                    type={showPwd ? 'text' : 'password'}
                    value={pwd}
                    onChange={(e) => setPwd(e.target.value)}
                    placeholder="8 caractères, 1 majuscule, 1 chiffre"
                    autoComplete="new-password"
                    className="w-full h-11 px-4 pr-11 rounded-xl bg-slate-50 border border-slate-200 text-sm focus:outline-none focus:bg-white focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100"
                    data-testid="reset-pwd-input"
                    disabled={state === 'submitting'}
                    required
                  />
                  <button type="button" onClick={() => setShowPwd((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    tabIndex={-1}>
                    {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </label>

              <label className="block mb-5">
                <span className="text-xs text-slate-600 font-medium">Confirmez le mot de passe</span>
                <input
                  type={showPwd ? 'text' : 'password'}
                  value={pwd2}
                  onChange={(e) => setPwd2(e.target.value)}
                  autoComplete="new-password"
                  className="w-full h-11 px-4 mt-1 rounded-xl bg-slate-50 border border-slate-200 text-sm focus:outline-none focus:bg-white focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100"
                  data-testid="reset-pwd2-input"
                  disabled={state === 'submitting'}
                  required
                />
              </label>

              <div className="space-y-1 mb-5 text-[11px]">
                <Rule ok={pwd.length >= 8}>Au moins 8 caractères</Rule>
                <Rule ok={/[A-Z]/.test(pwd)}>Au moins 1 majuscule</Rule>
                <Rule ok={/\d/.test(pwd)}>Au moins 1 chiffre</Rule>
                <Rule ok={pwdMatch}>Les deux champs correspondent</Rule>
              </div>

              <button type="submit" disabled={!canSubmit}
                className="w-full h-11 rounded-full bg-gradient-to-r from-indigo-500 to-violet-500 text-white text-sm font-semibold shadow hover:shadow-lg hover:from-indigo-600 hover:to-violet-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                data-testid="reset-submit-btn">
                {state === 'submitting' ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Enregistrement…</>
                ) : (
                  <><ShieldCheck className="w-4 h-4" /> Définir le mot de passe</>
                )}
              </button>
            </form>
          )}
        </div>

        <p className="text-center text-[11px] text-slate-400 mt-6">
          Lien sécurisé · chiffré HTTPS · expire automatiquement après 1h
        </p>
      </div>
    </div>
  );
}

function Rule({ ok, children }) {
  return (
    <div className={`flex items-center gap-2 ${ok ? 'text-emerald-600' : 'text-slate-400'}`}>
      {ok ? <Check className="w-3 h-3" /> : <span className="w-3 h-3 rounded-full border border-slate-300" />}
      <span>{children}</span>
    </div>
  );
}
